import React, { useEffect, useCallback, useState } from 'react';
import { Dialog, Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useSwipeable } from 'react-swipeable';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Share as CapacitorShare } from '@capacitor/share';
import { BASE_DOMAIN } from '../../api/config';

interface ImageGalleryProps {
    open: boolean;
    images: string[];
    currentImage: string | null;
    onClose: () => void;
    onSelect: (url: string) => void;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
    open,
    images,
    currentImage,
    onClose,
    onSelect
}) => {
    const [scale, setScale] = useState(1);
    const currentIndex = currentImage ? images.indexOf(currentImage) : -1;

    // Сброс масштаба при смене фото
    useEffect(() => {
        setScale(1);
    }, [currentImage, open]);

    const handleNext = useCallback(() => {
        if (currentIndex < images.length - 1) onSelect(images[currentIndex + 1]);
    }, [currentIndex, images, onSelect]);

    const handlePrev = useCallback(() => {
        if (currentIndex > 0) onSelect(images[currentIndex - 1]);
    }, [currentIndex, images, onSelect]);

    // 🚀 УЛЬТИМАТИВНЫЙ ШЕРИНГ (ФАЙЛ + СТРАХОВКА ОТ ВЫЛЕТА)
    const handleShare = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!currentImage) return;

        const absoluteUrl = currentImage.startsWith('http') 
            ? currentImage 
            : `${BASE_DOMAIN}${currentImage}`;

        try {
            // 1. Пробуем подготовить ФАЙЛ (для WhatsApp/Telegram)
            const response = await fetch(absoluteUrl);
            const blob = await response.blob();
            const file = new File([blob], 'orion_photo.jpg', { type: blob.type });

            // 🛡️ Проверяем, разрешит ли браузер отправить ФАЙЛ в текущем контексте
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: 'Изображение Orion',
                    text: 'Отправлено из Orion Messenger',
                });
            } else {
                // Если файлы не поддерживаются — шлем ссылку через Capacitor (самый стабильный метод)
                await CapacitorShare.share({ url: absoluteUrl });
            }
        } catch (err: any) {
            // 🚀 ГВАРДИЯ: Ловим AbortError (если юзер нажал Cancel) или NotAllowed (если жест потерян)
            const isUserCancel = err.name === 'AbortError' || err.name === 'NotAllowedError';
            
            if (!isUserCancel) {
                // Если произошла реальная ошибка (CORS и т.д.), шлем просто ссылку как последний шанс
                console.warn("Switching to Capacitor Share due to error:", err);
                await CapacitorShare.share({ url: absoluteUrl }).catch(() => {});
            } else {

            }
        }
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => { if (scale <= 1.05) handleNext(); },
        onSwipedRight: () => { if (scale <= 1.05) handlePrev(); },
        preventScrollOnSwipe: true,
        trackMouse: true,
        delta: 40
    });

    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, handleNext, handlePrev, onClose]);

    if (!currentImage) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            PaperProps={{ sx: { bgcolor: 'black', boxShadow: 'none', backgroundImage: 'none', overflow: 'hidden' } }}
        >
            <Box sx={{ position: 'relative', width: '100vw', height: '100vh', bgcolor: 'black', overflow: 'hidden' }}>
                
                {/* ЗАКРЫТЬ */}
                <IconButton 
                    onClick={onClose} 
                    sx={{ position: 'absolute', top: 20, right: 20, color: 'white', zIndex: 3000, bgcolor: 'rgba(0,0,0,0.5)' }}
                >
                    <CloseIcon fontSize="large" />
                </IconButton>

                {/* ПОДЕЛИТЬСЯ (Стрелочка внизу) */}
                <IconButton 
                    onClick={handleShare} 
                    sx={{ 
                        position: 'absolute', bottom: 30, left: 25, 
                        color: 'white', zIndex: 3000, bgcolor: 'rgba(255,255,255,0.15)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                    }}
                >
                    <IosShareIcon fontSize="medium" />
                </IconButton>

                <TransformWrapper
                    initialScale={1}
                    minScale={1}
                    maxScale={8}
                    centerOnInit={true}
                    centerZoomedOut={true}
                    limitToBounds={true}
                    panning={{ disabled: scale <= 1.05, velocityDisabled: true }}
                    onTransformed={(ref) => setScale(ref.state.scale)}
                    doubleClick={{ step: 0.5 }}
                >
                    <TransformComponent 
                        wrapperStyle={{ width: "100vw", height: "100vh" }} 
                        contentStyle={{ width: "100vw", height: "100vh" }}
                    >
                        <div 
                            {...swipeHandlers} 
                            style={{ 
                                width: '100vw', 
                                height: '100vh', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                touchAction: scale > 1.05 ? 'none' : 'pan-y' 
                            }}
                        >
                            <img
                                src={currentImage}
                                alt="gallery"
                                onDragStart={(e) => e.preventDefault()}
                                style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: '100%', 
                                    objectFit: 'contain', 
                                    display: 'block',
                                    willChange: 'transform'
                                }}
                            />
                        </div>
                    </TransformComponent>
                </TransformWrapper>

                {scale <= 1.05 && (
                    <>
                        {currentIndex > 0 && (
                            <IconButton onClick={(e) => { e.stopPropagation(); handlePrev(); }} sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'white', zIndex: 3000, bgcolor: 'rgba(255,255,255,0.1)' }}>
                                <ArrowBackIosNewIcon fontSize="large" />
                            </IconButton>
                        )}
                        {currentIndex < images.length - 1 && (
                            <IconButton onClick={(e) => { e.stopPropagation(); handleNext(); }} sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'white', zIndex: 3000, bgcolor: 'rgba(255,255,255,0.1)' }}>
                                <ArrowForwardIosIcon fontSize="large" />
                            </IconButton>
                        )}
                    </>
                )}

                <Typography sx={{ 
                    position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', 
                    color: 'white', bgcolor: 'rgba(0,0,0,0.5)', px: 2, py: 0.5, borderRadius: '15px', zIndex: 3000,
                    pointerEvents: 'none', fontSize: '0.9rem'
                }}>
                    {currentIndex + 1} / {images.length}
                </Typography>
            </Box>
        </Dialog>
    );
};

